/**
 * `src/lib/ui/reader/numerals.ts` — every number the reader draws, in Arabic.
 *
 * PURE. No DOM, no Svelte, no Dexie.
 *
 * WHY THIS FILE EXISTS AT ALL
 * ───────────────────────────
 * DECISIONS D2 makes the UI Arabic-only and `dir="rtl"` permanent, and
 * `pipeline/fixtures/font-subset.json` records that the UI subsets carry
 * **no Latin at all** — `amiri-regular.woff2` declares `U+0660-066D` and no
 * `U+0030-0039`. A Western-Arabic "255" in this app therefore does not merely
 * look foreign: it falls out of the subset, lands on whatever the platform
 * happens to have, and changes metrics mid-line. Every number the reader shows
 * goes through `arabicIndic()`.
 *
 * THE AYAH MARKER IS A FONT FEATURE, NOT A DRAWING
 * ────────────────────────────────────────────────
 * `U+06DD ARABIC END OF AYAH` is in the mushaf subset with the explicit note
 * "the rosette the ayah number is set inside", together with `U+0660`…`U+0669`.
 * So the marker is *text* — `۝` followed by the Arabic-Indic digits — and
 * the Amiri Quran face composes the rosette itself. A CSS circle with a number
 * in it would be a second, worse mushaf convention that does not scale with the
 * reading-size control and does not survive a font change.
 *
 * It is also `aria-hidden` wherever it is drawn: see `labels.ts` and the
 * accessibility section of `M2-STATUS.md`. A screen reader meeting U+06DD reads
 * either nothing or the raw codepoint name, and the ayah number is announced as
 * real Arabic prose instead.
 */

/** `U+0660 ARABIC-INDIC DIGIT ZERO`. The nine that follow are contiguous. */
const ARABIC_INDIC_ZERO = 0x0660;

/** `U+06DD ARABIC END OF AYAH` — the rosette the ayah number is set inside. */
export const END_OF_AYAH = '۝';

/**
 * Render a non-negative integer in Arabic-Indic digits.
 *
 * Total by construction: anything that is not a finite non-negative integer
 * becomes `'٠'` rather than `'NaN'`, because every caller is a label in a
 * reading surface and a thrown exception there costs the whole page.
 */
export function arabicIndic(value: number): string {
	if (!Number.isFinite(value) || value < 0) return '٠';
	const whole = Math.floor(value);
	const digits = String(whole);
	let out = '';
	for (const digit of digits) {
		out += String.fromCodePoint(ARABIC_INDIC_ZERO + (digit.codePointAt(0)! - 0x30));
	}
	return out;
}

/**
 * The end-of-ayah marker as it appears in the text run: the rosette codepoint
 * followed by the ayah number in Arabic-Indic digits.
 */
export function endOfAyahMark(ayah: number): string {
	return `${END_OF_AYAH}${arabicIndic(ayah)}`;
}
